import { api } from './api.js';
import { pickFromDrive } from './drivePicker.js';

// Pick ONE image out of the client's connected Google Drive, for the artwork
// fields (campaign hero images, journey steps, templates). Every source of
// artwork — upload, Content Studio, Drive — has to end up as the same ≤1000px
// JPEG data URL, because that's what the send pipeline stores and renders; a
// remote URL would break the moment sharing changed on the other side.
//
// The bytes come through Pulse's own authed route rather than a Google URL:
// canvas can't read a cross-origin image, and downscaling is the whole point.
//
// Returns the data URL, or '' when the picker was cancelled.
export async function pickDriveImage(entityId, { maxWidth = 1000, quality = 0.85 } = {}) {
  const [doc] = await pickFromDrive({
    getToken: () => api.myDrivePickerToken(entityId),
    mode: 'media',
    multiple: false,
  });
  if (!doc) return '';
  const res = await fetch(`/api/my/drive/${entityId}/media/${encodeURIComponent(doc.id)}`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not read that file from Drive.');
  const url = URL.createObjectURL(await res.blob());
  try { return await scaleToDataUrl(url, maxWidth, quality); }
  finally { URL.revokeObjectURL(url); }
}

export function scaleToDataUrl(src, maxWidth = 1000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error("Couldn't read that image."));
    img.src = src;
  });
}
